import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AdminInvoice {
  id: string;
  document_number: string;
  document_type: string;
  status: string;
  amount: number;
  vat_rate: number;
  vat_amount: number;
  total_amount: number;
  issued_at: string;
  due_date: string | null;
  paid_at: string | null;
  created_at: string;
  notes: string | null;
  order_id: string;
  shipper_company: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    address: string | null;
    siret: string | null;
    type: string;
  } | null;
  transporter_company: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    address: string | null;
    siret: string | null;
    type: string;
  } | null;
  order: {
    order_number: string;
    status: string;
    pickup_date: string;
    pickup_address: string | null;
    pickup_city: string | null;
    pickup_country: string | null;
    delivery_date: string;
    delivery_address: string | null;
    delivery_city: string | null;
    delivery_country: string | null;
    weight_kg: number;
    trailer_type: string;
    proposed_price: number | null;
    final_price: number | null;
  } | null;
}

export interface AdminInvoicesFilters {
  status?: string;
  date_from?: string;
  date_to?: string;
}

export const useAdminInvoices = (filters?: AdminInvoicesFilters) => {
  return useQuery({
    queryKey: ['admin-invoices', filters],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('billing_documents')
        .select(`
          *,
          shipper_company:companies!shipper_company_id(id, name, email, phone, address, siret, type),
          transporter_company:companies!transporter_company_id(id, name, email, phone, address, siret, type),
          order:orders!inner(order_number, status, pickup_date, pickup_address, pickup_city, pickup_country, delivery_date, delivery_address, delivery_city, delivery_country, weight_kg, trailer_type, proposed_price, final_price)
        `)
        .order('issued_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.date_from) {
        query = query.gte('issued_at', filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte('issued_at', filters.date_to);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as AdminInvoice[];
    },
  });
};

export const useMarkInvoicePaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { error } = await supabase
        .from('billing_documents')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
        })
        .eq('id', invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      toast.success('Invoice marked as paid');
    },
    onError: (error) => {
      console.error('Error marking invoice as paid:', error);
      toast.error('Error marking invoice as paid');
    },
  });
};

export const useReportInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invoiceId, reason }: { invoiceId: string; reason: string }) => {
      // Update invoice status
      const { error } = await supabase
        .from('billing_documents')
        .update({
          status: 'reported',
          notes: reason,
        })
        .eq('id', invoiceId);

      if (error) throw error;

      // Send notification email
      try {
        const { error: emailError } = await supabase.functions.invoke(
          'send-invoice-report-notification',
          {
            body: { invoiceId, reason },
          }
        );

        if (emailError) {
          console.error('Error sending notification email:', emailError);
          // Don't throw - we still want to show success for the report itself
        }
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
        // Don't throw - we still want to show success for the report itself
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      toast.success('Invoice reported and notifications sent');
    },
    onError: (error) => {
      console.error('Error reporting invoice:', error);
      toast.error('Error reporting invoice');
    },
  });
};

export const useCancelInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const { error } = await supabase
        .from('billing_documents')
        .update({
          status: 'cancelled',
        })
        .eq('id', invoiceId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-invoices'] });
      toast.success('Invoice cancelled');
    },
    onError: (error) => {
      console.error('Error cancelling invoice:', error);
      toast.error('Error cancelling invoice');
    },
  });
};
